<!-- CustomizeProductSection.vue - Section for selecting imprint method and customizing product -->
<template>
  <div class="customize-product-section">
    <!-- Imprint Selection Instructions -->
    <div class="imprint-selection">
      <!-- Divider Line -->
      <div class="divider-line"></div>

      <!-- Instructional Text -->
      <div class="instruction-text">
        <p>{{ instructionText }}</p>
        <p v-html="instructionTextAdditional"></p>
      </div>

      <!-- Method Selection Buttons -->
      <div class="method-buttons">
        <BaseButton
          v-for="method in artworkMethods"
          :key="method.id"
          :text="method.label"
          :variant="selectedArtworkMethod === method.id ? 'primary' : 'secondary'"
          size="medium"
          :icon="iconComponents[method.iconName]"
          icon-position="top"
          class="method-button"
          @click="selectMethod(method.id)"
        />
      </div>

      <!-- Divider Line -->
      <div class="divider-line"></div>
    </div>

    <!-- Method-specific content -->
    <div v-if="selectedArtworkMethod" class="method-content">
      <!-- Start New Imprint -->
      <div v-if="selectedArtworkMethod === 'new'" class="start-new-imprint">
        <!-- Multiple Imprint Modules -->
        <NewImprintModule
          v-for="(imprint, index) in newImprintStore.imprints"
          :key="index"
          :title="getImprintTitle(index)"
          :imprint-key="index"
          :collapsible="true"
          :initial-collapsed="false"
          :deletable="index > 0"
          @delete-requested="handleDeleteImprint"
        >
          <StartNewImprintForm
            :imprint-key="index"
            @method-changed="handleImprintMethodChanged"
            @location-changed="handleLocationChanged"
            @message-type-changed="handleMessageTypeChanged"
            @lines-changed="handleLinesChanged"
            @image-uploaded="handleImageUploaded"
            @image-removed="handleImageRemoved"
            @special-instructions-changed="handleSpecialInstructionsChanged"
          />
        </NewImprintModule>

        <!-- Add Another Imprint Location Button -->
        <!-- Hidden when:
             - Envelop product
             - No more locations available (canAddMoreImprints)
             - Product has no additional_location_fee tiers configured (hasAdditionalLocationFeeData)
             -->
        <AddImprintLocationButton
          v-if="!appStore.isEnvelopProduct && canAddMoreImprints"
          @add-imprint="handleAddImprint"
        />
      </div>

      <!-- Send Artwork - Email Instructions -->
      <div v-else-if="selectedArtworkMethod === 'send'" class="method-panel">
        <NewImprintModule
          title="Send Us Your Artwork"
          :collapsible="false"
          :deletable="false"
        >
          <!-- Imprint Method Selector -->
          <ImprintMethodSelector
            @method-selected="handleImprintMethodChanged"
          />
          
          <!-- Imprint Location Selector -->
          <ImprintLocationSelector
            label="2. Choose an Imprint Location"
            @location-selected="handleLocationChanged"
          />
          
          <!-- Imprint Details -->
          <div class="special-instructions-section">
            <label class="section-label">3. Imprint Details</label>
            <textarea
              v-model="sendArtworkInstructions"
              rows="5"
              placeholder="Provide any details you would like the art team to know about your artwork to create your proof"
              @input="handleSendArtworkInstructionsChange"
            ></textarea>
          </div>
        </NewImprintModule>
      </div>

      <!-- Use Previous Artwork -->
      <div v-else-if="selectedArtworkMethod === 'previous'" class="method-panel">
        <NewImprintModule
          title="Use My Previous Artwork"
          :collapsible="false"
          :deletable="false"
        >
          <!-- Imprint Method Selector -->
          <ImprintMethodSelector
            @method-selected="handleImprintMethodChanged"
          />
          
          <!-- Imprint Location Selector -->
          <ImprintLocationSelector
            label="2. Choose an Imprint Location"
            @location-selected="handleLocationChanged"
          />
          
          <!-- Imprint Details -->
          <div class="special-instructions-section">
            <label class="section-label">3. Imprint Details</label>
            <textarea
              v-model="previousArtworkInstructions"
              rows="5"
              placeholder="Provide any details you would like the art team to know about your artwork to create your proof"
              @input="handlePreviousArtworkInstructionsChange"
            ></textarea>
          </div>
        </NewImprintModule>
      </div>

      <!-- Design Online -->
      <div v-else-if="selectedArtworkMethod === 'design'" class="method-panel">
        <NewImprintModule
          title="Design Online"
          :collapsible="false"
          :deletable="false"
        >
          <!-- ✅ Design Online Component (Real API) -->
          <DesignOnlineSimple
            :product-id="appStore.mainProductId"
            :sku="appStore.productSku"
            @design-returned="handleDesignReturned"
          />
        </NewImprintModule>
      </div>
    </div>
    
    <!-- Navigation Buttons -->
    <div class="navigation-buttons">
      <BaseButton
        :text="previousStepText"
        variant="outline"
        size="medium"
        @click="handlePreviousStep"
      />
      <BaseButton
        :text="nextStepButtonText"
        variant="primary"
        size="medium"
        @click="handleNextStep"
      />
    </div>
    
    <!-- State Debugger (only for development) -->
    <StateDebuggerNewImprint v-if="debugConfig.showStateDebugger" />
    
    <!-- Cart Payload Tester (only for development) -->
    <CartPayloadTester v-if="debugConfig.showCartPayloadTester" />

    <!-- Confirm Dialog (global, driven by useDialogStore) -->
    <ConfirmDialog />
  </div>
</template>

<script>
import BaseButton from '../components/BaseButton.vue';
import NewImprintModule from '../components/NewImprintModule.vue';
import StartNewImprintForm from '../components/StartNewImprintForm_Pinia.vue';
import AddImprintLocationButton from '../components/AddImprintLocationButton.vue';
import ImprintMethodSelector from '../components/ImprintMethodSelector.vue';
import ImprintLocationSelector from '../components/ImprintLocationSelector.vue';
import StateDebuggerNewImprint from '../components/StateDebuggerNewImprint.vue';
import CartPayloadTester from '../components/test/CartPayloadTester.vue';
import ConfirmDialog from '../components/ConfirmDialog.vue';
import {
    ARTWORK_METHODS,
    ARTWORK_INSTRUCTION_TEXT,
    getArtworkInstructionTextAdditional
} from '../static_data/artworkMethods.js';
// ✅ NEW: Design Online Component
import DesignOnlineSimple from '@/features/design/components/DesignOnlineSimple.vue';
import { IMPRINT_TYPES } from '../static_data/imprintTypes.js';
import { useImprintStore } from '../stores/useImprintStore.js';
import { useNewImprintStore } from '../stores/useNewImprintStore.js';
import { useSelectionStore } from '../stores/useSelectionStore.js';
import { useAppStore } from '../stores/useAppStore.js';
// ✅ ISSUE 1: Design Online Store for cart integration
import { useDesignOnlineStore } from '@/features/design/stores/useDesignOnlineStore.js';
import { useDialogStore } from '../stores/useDialogStore.js';
import { useSectionValidation } from '../composables/useSectionValidation.js';
import { DEBUG_CONFIG } from '../config/debug.js';
import {
  IconNewImprint,
  IconSendArtwork,
  IconPreviousArtwork,
  IconDesignOnline
} from '../components/icons';
import {getColorName} from "../static_data/sections";

export default {
  name: 'CustomizeProductSection',

  components: {
    BaseButton,
    NewImprintModule,
    StartNewImprintForm,
    AddImprintLocationButton,
    ImprintMethodSelector,
    ImprintLocationSelector,
    StateDebuggerNewImprint,
    CartPayloadTester,
    DesignOnlineSimple,  // ✅ NEW
    ConfirmDialog
  },

  props: {
    productData: {
      type: Object,
      default: () => ({})
    }
  },

  setup() {
    const newImprintStore = useNewImprintStore();
    const imprintStore = useImprintStore();
    const selectionStore = useSelectionStore();
    const appStore = useAppStore();
    const designOnlineStore = useDesignOnlineStore(); // ✅ ISSUE 1: Add Design Online store
    const dialogStore = useDialogStore();
    const { step2Validation } = useSectionValidation();
    
    return {
      newImprintStore,
      imprintStore,
      selectionStore,
      appStore,
      designOnlineStore, // ✅ ISSUE 1: Expose to component
      dialogStore,
      step2Validation,
      debugConfig: DEBUG_CONFIG
    };
  },

  data() {
    // Find default artwork method
    const defaultMethod = ARTWORK_METHODS.find(m => m.isDefault);
    
    return {
      selectedArtworkMethod: defaultMethod ? defaultMethod.id : null,
      imprintTypes: IMPRINT_TYPES,
      instructionText: ARTWORK_INSTRUCTION_TEXT.trim(),
      instructionTextAdditional: getArtworkInstructionTextAdditional().trim(),
      
      // Counter: how many times "Add Another Imprint Location" was clicked.
      // Resets on page reload (in-memory only, not persisted).
      // Dialog is shown only on the first click.
      addImprintClickCount: 0,

      // Send Us Your Artwork data
      sendArtworkData: {
        imprintMethod: null,
        location: null,
        instructions: ''
      },
      sendArtworkInstructions: '',
      
      // Use My Previous Artwork data
      previousArtworkData: {
        imprintMethod: null,
        location: null,
        previousArtwork: null, // TODO: from PreviousArtworkSelector
        referenceOrderNumber: null,
        instructions: ''
      },
      previousArtworkInstructions: '',
      
      // Map icon names to components
      iconComponents: {
        IconNewImprint,
        IconSendArtwork,
        IconPreviousArtwork,
        IconDesignOnline
      }
    };
  },

  computed: {
    // Filter out 'Design Online' if isSupported is explicitly false (no metafield or API said no)
    artworkMethods() {
      if (this.designOnlineStore.isSupported === false) {
        return ARTWORK_METHODS.filter(m => m.id !== 'design');
      }
      return ARTWORK_METHODS;
    },

    currentMethodData() {
      return this.artworkMethods.find(m => m.id === this.selectedArtworkMethod) || {};
    },
    
    /**
     * Check if can add more imprints.
     * Hide "Add Another Imprint Location" button if:
     * - Product supports only 1 location total
     * - All available locations are already used across existing imprints
     */
    canAddMoreImprints() {
        const totalLocations = this.imprintStore.availableLocations?.length || 0;
        const availableForNew = this.newImprintStore.availableLocationsForNewImprint?.length || 0;
        const currentImprints = this.newImprintStore.imprints?.length || 0;

        console.log('[CustomizeProductSection] canAddMoreImprints check:', {
            totalLocations,
            availableForNew,
            currentImprints
        });

        // Hide if product supports only 1 location
        if (totalLocations <= 1) return false;

        // ✅ HARD CAP: do not allow creating more imprint blocks than total locations
        // (even if user hasn't selected a location yet)
        if (currentImprints >= totalLocations) return false;

        // Hide if all locations are already used
        return availableForNew > 0;
    },

    /**
     * Dynamic text for Next Step button
     * Changes based on whether Add-Ons section is visible
     */
    nextStepButtonText() {
      if (this.debugConfig.showAddOnsSection) {
        return 'Next Step: Add-Ons';
      }
      return 'Next Step: Review';
    },
    previousStepText() {
      return `Previous Step: ${getColorName()} & Quantity`;
    }
  },

  methods: {
    /**
     * Get imprint title based on index
     */
    getImprintTitle(index) {
      const titles = ['First Imprint', 'Second Imprint', 'Third Imprint', 'Fourth Imprint', 'Fifth Imprint'];
      return titles[index] || `Imprint ${index + 1}`;
    },

    /**
     * Select artwork method
     */
    selectMethod(methodId) {
      // Close all color pickers and clear pending selection across all imprints
      // before switching artwork methods. The 'new' form unmounts (v-if) but the
      // Pinia store persists, so stale showColorPicker / pendingColorSelection
      // can cause pickers to appear under wrong lines when returning to 'new'.
      this.newImprintStore.imprints.forEach(imprint => {
        imprint.textLines.forEach(line => { line.showColorPicker = false; });
        imprint.pendingColorSelection = { lineId: null, color: null, colorObj: null, customColor: false };
      });

      this.selectedArtworkMethod = methodId;
      
      // Save to Pinia store
      const imprintStore = useImprintStore();
      imprintStore.setSelectedArtworkMethod(methodId);
      
      this.$emit('artwork-method-selected', methodId);
      console.log('[CustomizeProductSection] Artwork method selected:', methodId);
    },
    
    /**
     * Add new imprint.
     * Shows a confirm dialog (text from theme settings) before adding the second+ location.
     * First imprint is added without a dialog (no extra fees incurred).
     */
    async handleAddImprint() {
        const totalLocations = this.imprintStore.availableLocations?.length || 0;
        const currentImprints = this.newImprintStore.imprints?.length || 0;

        // Safety guard: block add if we've reached the max possible imprint blocks
        if (totalLocations > 0 && currentImprints >= totalLocations) {
            console.log('[CustomizeProductSection] Add imprint blocked: reached max imprints for available locations', {
                totalLocations,
                currentImprints
            });
            return;
        }

        // Increment in-memory click counter (resets on page reload)
        this.addImprintClickCount++;

        // Show warning dialog only on the FIRST click.
        // Subsequent clicks skip the dialog and add directly.
        const isFirstClick = this.addImprintClickCount === 1;

        if (currentImprints >= 1 && isFirstClick) {
            const confirmed = await this.dialogStore.confirm('add_imprint_location');
            if (!confirmed) {
                // User cancelled — roll back the counter so the dialog
                // appears again on the next button press.
                this.addImprintClickCount--;
                console.log('[CustomizeProductSection] Add imprint cancelled by user');
                return;
            }
        }

        this.newImprintStore.addNewImprint();
        console.log('[CustomizeProductSection] New imprint added, total:', this.newImprintStore.imprints.length,
            '| click #', this.addImprintClickCount);
    },
    
    /**
     * Delete imprint
     */
    handleDeleteImprint(imprintKey) {
      console.log('[CustomizeProductSection] Delete imprint requested:', imprintKey);
      this.newImprintStore.deleteImprint(imprintKey);
    },
    
    // Send Artwork Handlers
    handleSendArtworkInstructionsChange() {
      this.sendArtworkData.instructions = this.sendArtworkInstructions;
      console.log('[CustomizeProductSection] Send artwork instructions:', this.sendArtworkInstructions);

      // Save to newImprintStore so payloadBuilders can access it
      if (this.newImprintStore.imprints[0] !== undefined) {
        this.newImprintStore.imprints[0].specialInstructions = this.sendArtworkInstructions;
      }

      this.$emit('special-instructions-changed', {
        method: 'send',
        instructions: this.sendArtworkInstructions
      });
    },
    
    // Previous Artwork Handlers  
    handlePreviousArtworkInstructionsChange() {
      this.previousArtworkData.instructions = this.previousArtworkInstructions;
      console.log('[CustomizeProductSection] Previous artwork instructions:', this.previousArtworkInstructions);

      // Save to newImprintStore so payloadBuilders can access it
      if (this.newImprintStore.imprints[0] !== undefined) {
        this.newImprintStore.imprints[0].specialInstructions = this.previousArtworkInstructions;
      }

      this.$emit('special-instructions-changed', {
        method: 'previous',
        instructions: this.previousArtworkInstructions
      });
    },
    
    /**
     * Transform Send Us Your Artwork data to cart payload format
     * Returns imprint_lines and properties objects ready for cart
     * @returns {Object} { imprint_lines, properties }
     */
    transformSendArtworkToCart() {
      if (!this.sendArtworkData.imprintMethod || !this.sendArtworkData.location) {
        console.warn('[CustomizeProductSection] Send Artwork: Missing required data');
        return null;
      }
      
      const properties = {
        "Imprint location": this.sendArtworkData.location.title || '',
        "_Imprint Location SKU": this.sendArtworkData.location.sku || '',
        
        "Imprint Method": this.sendArtworkData.imprintMethod.name || '',
        "_Imprint Method SKU": this.sendArtworkData.imprintMethod.sku || '',
        
        "Special Instruction": `Send Us Your Artwork After Ordering. \r\n\r\nComment: ${this.sendArtworkData.instructions} \r\n\r\n ${this.selectionStore.specialInstructions}`,
        
        "_price_details": {
          setup_fee: 30,      // Base setup fee
          proof_fee: 5,       // Standard proof fee
          image_fee: 0,       // May be 25 if image expected
          customColourFee: 0, // No custom colors yet
          running_fee: 0      // No running fee (colors unknown)
        }
      };
      
      console.log('[CustomizeProductSection] Send Artwork transform:', properties);
      
      return {
        imprint_lines: {},  // Empty - artwork will be sent later
        properties: properties
      };
    },
    
    /**
     * Transform Use My Previous Artwork data to cart payload format
     * Returns imprint_lines and properties objects ready for cart
     * @returns {Object} { imprint_lines, properties }
     */
    transformPreviousArtworkToCart() {
      if (!this.previousArtworkData.imprintMethod || !this.previousArtworkData.location) {
        console.warn('[CustomizeProductSection] Previous Artwork: Missing required data');
        return null;
      }
      
      const properties = {
        "Imprint location": this.previousArtworkData.location.title || '',
        "_Imprint Location SKU": this.previousArtworkData.location.sku || '',
        
        "Imprint Method": this.previousArtworkData.imprintMethod.name || '',
        "_Imprint Method SKU": this.previousArtworkData.imprintMethod.sku || '',
        
        "Special Instruction": `Use My Previous Artwork.\r\n\r\nComment: ${this.previousArtworkData.instructions}`,
        
        "_price_details": {
          setup_fee: 0,       // May be 0 if setup already exists
          proof_fee: 0,       // May be 0 if proof not needed
          image_fee: 0,       // Artwork already exists
          customColourFee: 0,
          running_fee: 0      // Colors already known from previous
        }
      };
      
      // Add optional previous artwork reference if available
      if (this.previousArtworkData.previousArtwork) {
        properties["_Previous Artwork ID"] = this.previousArtworkData.previousArtwork.id || '';
      }
      if (this.previousArtworkData.referenceOrderNumber) {
        properties["_Reference Order"] = this.previousArtworkData.referenceOrderNumber;
      }
      
      console.log('[CustomizeProductSection] Previous Artwork transform:', properties);
      
      return {
        imprint_lines: {},  // Empty - artwork already exists
        properties: properties
      };
    },

    /**
     * ✅ Transform Design Online data to cart payload format
     * Returns imprint_lines and properties objects ready for cart
     * @returns {Object|null} { imprint_lines, properties } or null if not ready
     */
    transformDesignOnlineToCart() {
      const store = this.designOnlineStore;

      console.log('[AddToCart] 🎨 transformDesignOnlineToCart() called');
      console.log('[AddToCart] Design Online store state:', {
        stage: store.stage,
        hasDesign: store.hasDesign,
        isReady: store.isReady,
        isApproved: store.isApproved,
        tokenId: store.tokenId,
        hasPreview: !!store.previewImage,
        hasPdfPreview: !!store.pdfPreviewUrl,
        hasPrintReady: !!store.printReadyFileUrl
      });

      // Check if design is ready (user approved and all data present)
      if (!store.isReady) {
        console.error('[AddToCart] ❌ Design Online: Not ready!');
        console.error('[AddToCart] isReady breakdown:', {
          hasTokenId: !!store.tokenId,
          hasPreview: !!(store.previewImage || store.pdfPreviewUrl),
          isApproved: store.isApproved,
          allConditions: `tokenId=${!!store.tokenId} && preview=${!!(store.previewImage || store.pdfPreviewUrl)} && approved=${store.isApproved}`
        });
        console.error('[AddToCart] 💡 User must approve design by checking the checkbox!');
        return null;
      }

      // Get line item properties from store
      const props = store.lineItemProperties;

      console.log('[AddToCart] lineItemProperties:', props);

      if (!props || !props.token_id) {
        console.error('[AddToCart] ❌ Missing token_id!');
        console.error('[AddToCart] lineItemProperties:', props);
        return null;
      }

      // ✅ Build upcharge objects from imprintStore
      const sizeUpchargeObj = {};
      const colorUpchargeObj = {};
      
      // Get size upcharges
      if (this.imprintStore.sizeUpcharge) {
        Object.keys(this.imprintStore.sizeUpcharge).forEach(size => {
          if (this.imprintStore.sizeUpcharge[size] !== 0) {
            sizeUpchargeObj[size] = this.imprintStore.sizeUpcharge[size];
          }
        });
      }
      
      // Get color upcharges
      if (this.imprintStore.colorUpcharge) {
        Object.keys(this.imprintStore.colorUpcharge).forEach(colorKey => {
          const colorData = this.imprintStore.colorUpcharge[colorKey];
          if (colorData && colorData.price !== 0) {
            colorUpchargeObj[colorKey] = {
              price: colorData.price || 0,
              price_type: colorData.price_type || ''
            };
          }
        });
      }
      
      console.log('[AddToCart] Design Online upcharges:', {
        sizeUpcharge: sizeUpchargeObj,
        colorUpcharge: colorUpchargeObj
      });

      const properties = {
        // ✅ REQUIRED: Design Online specific properties (with underscore prefix)
        "_token_id": props.token_id,
        "_pdf_preview_url": props.pdf_preview_url || '',
        "_print_ready_file_url": props.print_ready_file_url || '',
        
        // ✅ OPTIONAL: Additional design data
        "_design_url": store.editUrl || '',
        "_preview_image": props.preview_image || '',

        // Standard imprint properties
        "Imprint Method": "Design Online",
        "_Imprint Method SKU": "DESIGN_ONLINE",

        // ✅ NEW: Size and Color upcharges (from Step 1)
        "_sizeUpcharge": sizeUpchargeObj,
        "_colorUpcharge": colorUpchargeObj,

        // Price details (no setup/proof fees for Design Online)
        "_price_details": {
          setup_fee: 0,       // No setup fee - design is ready
          proof_fee: 0,       // No proof fee - user approved design
          image_fee: 0,       // Included in design
          customColourFee: 0, // No custom colors
          running_fee: 0      // Included in design
        }
      };

      console.log('[AddToCart] ✅ Design Online payload created:', properties);

      return {
        imprint_lines: {},  // Empty - no separate lines for Design Online
        properties: properties
      };
    },

    /**
     * Get cart payload based on selected artwork method
     * @returns {Object|null} Cart payload or null if invalid
     */
    getCartPayload() {
      console.log('[AddToCart] 📦 getCartPayload() called, method:', this.selectedArtworkMethod);
      
      let payload = null;
      
      if (this.selectedArtworkMethod === 'new') {
        console.log('[AddToCart] ➡️ Using Start New Imprint transform');
        payload = this.newImprintStore.transformToCartPayload();
      } else if (this.selectedArtworkMethod === 'send') {
        console.log('[AddToCart] ➡️ Using Send Artwork transform');
        payload = this.transformSendArtworkToCart();
      } else if (this.selectedArtworkMethod === 'previous') {
        console.log('[AddToCart] ➡️ Using Previous Artwork transform');
        payload = this.transformPreviousArtworkToCart();
      } else if (this.selectedArtworkMethod === 'design') {
        console.log('[AddToCart] ➡️ Using Design Online transform');
        payload = this.transformDesignOnlineToCart();
      } else {
        console.error('[AddToCart] ❌ Unknown artwork method:', this.selectedArtworkMethod);
        return null;
      }
      
      if (payload) {
        console.log('[AddToCart] ✅ Payload generated successfully');
        console.log('[AddToCart] Payload preview:', {
          hasImprintLines: !!payload.imprint_lines,
          imprintLinesCount: Object.keys(payload.imprint_lines || {}).length,
          hasProperties: !!payload.properties,
          propertiesCount: Object.keys(payload.properties || {}).length,
          method: payload.properties?.['Imprint Method'],
          methodSku: payload.properties?.['_Imprint Method SKU']
        });
      } else {
        console.error('[AddToCart] ❌ Payload is NULL - cannot add to cart!');
      }
      
      return payload;
    },
    
    // Start New Imprint Form Handlers
    handleImprintMethodChanged(method) {
      console.log('[CustomizeProductSection] Imprint method changed:', {
        sku: method?.sku,
        name: method?.name,
        currentArtworkMethod: this.selectedArtworkMethod
      });
      
      // Save method based on selected artwork method
      if (this.selectedArtworkMethod === 'send') {
        this.sendArtworkData.imprintMethod = method;
        
        // ✅ CRITICAL FIX: ALSO save to imprintStore (used by cart!)
        // buildSendArtworkPayload() reads from imprintStore.selectedImprintMethod
        this.imprintStore.setSelectedImprintMethod(method);
        
        console.log('[CustomizeProductSection] Saved to sendArtworkData + imprintStore');
      } else if (this.selectedArtworkMethod === 'previous') {
        this.previousArtworkData.imprintMethod = method;
        
        // ✅ CRITICAL FIX: ALSO save to imprintStore (used by cart!)
        // buildPreviousArtworkPayload() reads from imprintStore.selectedImprintMethod
        this.imprintStore.setSelectedImprintMethod(method);
        
        console.log('[CustomizeProductSection] Saved to previousArtworkData + imprintStore');
      }
      // Note: 'new' method already uses imprintStore
    },
    
    handleLocationChanged(data) {
      console.log('[CustomizeProductSection] Location changed:', {
        sku: data?.sku,
        locationTitle: data?.location?.title,
        currentMethod: this.selectedArtworkMethod
      });
      
      // ✅ FIX: Extract location object from event data
      // ImprintLocationSelector emits: { sku: "FRC", location: {...} }
      // We need to save the location object, not the entire event data
      const location = data?.location || data;
      
      // Save location based on selected artwork method
      if (this.selectedArtworkMethod === 'send') {
        this.sendArtworkData.location = location;
        
        // ✅ CRITICAL FIX: ALSO save to newImprintStore (used by cart!)
        // buildSendArtworkPayload() reads from newImprintStore.currentImprint.selectedLocation
        this.newImprintStore.setLocation(location, 0);
        
        console.log('[CustomizeProductSection] Saved to sendArtworkData + newImprintStore:', location?.title);
      } else if (this.selectedArtworkMethod === 'previous') {
        this.previousArtworkData.location = location;
        
        // ✅ CRITICAL FIX: ALSO save to newImprintStore (used by cart!)
        // buildPreviousArtworkPayload() reads from newImprintStore.currentImprint.selectedLocation
        this.newImprintStore.setLocation(location, 0);
        
        console.log('[CustomizeProductSection] Saved to previousArtworkData + newImprintStore:', location?.title);
      }
      // Note: 'new' method already uses store (useNewImprintStore)
    },
    
    handleMessageTypeChanged(type) {
      console.log('[CustomizeProductSection] Message type changed:', type);
    },
    
    handleLinesChanged(lines) {
      console.log('[CustomizeProductSection] Lines changed:', lines);
    },
    
    handleImageUploaded(data) {
      console.log('[CustomizeProductSection] Image uploaded:', data);
    },
    
    handleImageRemoved() {
      console.log('[CustomizeProductSection] Image removed');
    },
    
    handleSpecialInstructionsChanged(text) {
      console.log('[CustomizeProductSection] Special instructions changed:', text);
    },
    
    /**
     * Handle Previous Step button click
     * Navigate back to Colour & Quantity section
     */
    handlePreviousStep() {
      console.log('[CustomizeProductSection] Previous step clicked');
      this.$emit('previous-step');
    },
    
    /**
     * Handle Next Step button click
     * Validate current section and proceed to Add-Ons
     */
    handleNextStep() {
      console.log('[CustomizeProductSection] Next step clicked');

      // TODO: Add validation before proceeding
      // const errors = this.step2Validation.validateAll();
      // if (errors.length > 0) {
      //   console.warn('[CustomizeProductSection] Validation errors:', errors);
      //   return;
      // }
      
      this.$emit('next-step');
    },

    /**
     * ✅ NEW: Handle design-returned event from DesignOnlineSimple
     * Called when user returns from Studio with created design
     */
    handleDesignReturned() {
      console.log('[AUTO_SWITCH] 📨 handleDesignReturned() event received from DesignOnlineSimple');

      // Auto-select Design Online method
      this.selectedArtworkMethod = 'design';
      console.log('[AUTO_SWITCH] Set selectedArtworkMethod = "design"');

      // Save to store
      const imprintStore = useImprintStore();
      imprintStore.setSelectedArtworkMethod('design');

      // Emit event to parent (will trigger navigation and scroll)
      console.log('[AUTO_SWITCH] Emitting design-online-returned to parent');
      this.$emit('design-online-returned');

      console.log('[AUTO_SWITCH] ✅ handleDesignReturned() complete');
    }
  },

  watch: {
    // ✅ Watch for Design Online data changes
    'designOnlineStore.stage': {
      handler(newStage, oldStage) {
        console.log('[AUTO_SWITCH] 👀 Stage changed:', { oldStage, newStage, hasDesign: this.designOnlineStore.hasDesign });

        // Auto-switch to Design Online tab when data arrives
        if (newStage === 'preview' && this.designOnlineStore.hasDesign) {
          console.log('[AUTO_SWITCH] 🎨 Design data detected - auto-switching to Design Online tab');

          // Don't switch if already on Design Online
          if (this.selectedArtworkMethod === 'design') {
            console.log('[AUTO_SWITCH] Already on Design Online tab, skipping');
            return;
          }

          // Auto-select Design Online method
          this.selectedArtworkMethod = 'design';

          // Save to store
          const imprintStore = useImprintStore();
          imprintStore.setSelectedArtworkMethod('design');

          // Emit event to parent (will trigger navigation and scroll)
          this.$emit('design-online-returned');

          console.log('[AUTO_SWITCH] ✅ Switched to Design Online tab');
        }
      },
      immediate: false
    }
  },

  mounted() {
    console.log('[AUTO_SWITCH] CustomizeProductSection.mounted() - START');

    // Check Design Online availability upfront so the button shows/hides before user interaction.
    // initProductId() reads #ops_product_id from DOM — if missing, sets isSupported=false immediately.
    // checkSupport() Phase 1 uses design_enabled metafield shortcut (no Studio API call).
    const opsProductId = this.designOnlineStore.initProductId();
    if (opsProductId) {
      this.designOnlineStore.checkSupport(opsProductId, null, {
        design_enabled: true,
        force_probe: false
      }).then(() => {
        console.log('[AUTO_SWITCH] Design Online support check done, isSupported:', this.designOnlineStore.isSupported);
      });
    }

    // ✅ CRITICAL: Restore all data after redirect/reload
    const restoredImprint = this.newImprintStore.restoreFromSessionStorage();
    const restoredSelection = this.selectionStore.restoreFromSessionStorage();

    if (restoredImprint || restoredSelection) {
      console.log('[AUTO_SWITCH] ✅ Restored imprint/selection data');
    }

    // ✅ CRITICAL FIX: Manually restore Design Online data BEFORE checking stage
    // This is necessary because DesignOnlineSimple may not mount if selectedArtworkMethod !== 'design'
    console.log('[AUTO_SWITCH] 🔍 Manually restoring Design Online data...');
    const restoredDesign = this.designOnlineStore.restoreFromStorage();
    console.log('[AUTO_SWITCH] Design Online restore result:', restoredDesign);

    // Also check URL parameters for return from Studio
    const { tokenId, exParam } = this.designOnlineStore.readReturnParamsFromUrl();
    if (tokenId && exParam) {
      console.log('[AUTO_SWITCH] 🔗 Detected URL parameters from Studio return:', { tokenId, exParam });
      console.log('[AUTO_SWITCH] 🔄 Calling handleReturn() to fetch design data from API...');

      // Call handleReturn to fetch design data from Studio API
      // This is async, so watcher will trigger when stage changes to 'preview'
      this.designOnlineStore.handleReturn(tokenId, exParam).then(success => {
        console.log('[AUTO_SWITCH] handleReturn() completed:', success);
      }).catch(error => {
        console.error('[AUTO_SWITCH] handleReturn() failed:', error);
      });
    }

    // ✅ NEW: Check for existing Design Online data on mount
    console.log('[AUTO_SWITCH] Checking Design Online state:', {
      stage: this.designOnlineStore.stage,
      hasDesign: this.designOnlineStore.hasDesign
    });

    if (this.designOnlineStore.stage === 'preview' && this.designOnlineStore.hasDesign) {
      console.log('[AUTO_SWITCH] 🎨 Detected existing Design Online data - auto-switching tab');

      // Auto-select Design Online method
      this.selectedArtworkMethod = 'design';

      // Save to store
      const imprintStore = useImprintStore();
      imprintStore.setSelectedArtworkMethod('design');

      // Emit event to parent (will trigger navigation and scroll)
      this.$nextTick(() => {
        console.log('[AUTO_SWITCH] Emitting design-online-returned event');
        this.$emit('design-online-returned');
      });

      console.log('[AUTO_SWITCH] ✅ Switched to Design Online tab');
    } else {
      console.log('[AUTO_SWITCH] No Design Online data found, using default method:', this.selectedArtworkMethod);

      // Save default artwork method to store and emit on mount
      if (this.selectedArtworkMethod) {
        const imprintStore = useImprintStore();
        imprintStore.setSelectedArtworkMethod(this.selectedArtworkMethod);
        this.$emit('artwork-method-selected', this.selectedArtworkMethod);
      }
    }
    
    console.log('[AUTO_SWITCH] CustomizeProductSection.mounted() - END:', {
      imprints: this.newImprintStore.imprints.length,
      selectedMethod: this.selectedArtworkMethod,
      designStage: this.designOnlineStore.stage
    });
  },
  
  created() {
    // Debug: log available methods
    console.log('[CustomizeProductSection] Available transform methods:', {
      transformToCartPayload: typeof this.newImprintStore.transformToCartPayload,
      transformSendArtworkToCart: typeof this.transformSendArtworkToCart,
      transformPreviousArtworkToCart: typeof this.transformPreviousArtworkToCart,
      transformDesignOnlineToCart: typeof this.transformDesignOnlineToCart, // ✅ ISSUE 1: Added
      getCartPayload: typeof this.getCartPayload
    });
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;
.customize-product-section {
  width: 100%;
}

// Imprint Selection
.imprint-selection {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: 20px;
}

// Divider Line
.divider-line {
  width: 100%;
  height: 0;
  border-top: 1px solid $color-border;
}

// Instruction Text
.instruction-text {
  width: 100%;
  text-align: left;  // ADDED: align text to the left
  
  p {
    font-family: $font-family-base;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: $color-text-darker;
    margin: 0;
    white-space: pre-line;
    padding-bottom: 15px;
  }
}

// Method Selection Buttons
.method-buttons {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 25px;
  }
}

.method-button {
  flex: 0 1 auto;
  width: 170px;
  height: 89px;
  
  @media (max-width: 768px) {
    flex: 1 1 100%;
    width: 100%;
    min-width: auto;
    max-width: 100%;
  }
}

// Method-specific Content
.method-content {
  margin-top: 20px;
  width: 100%;
}

.start-new-imprint {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 20px;
}

.method-panel {
  width: 100%;
}

// Method Instructions
.method-instructions {
  width: 100%;
  text-align: left;
  
  p {
    font-family: $font-family-base;
    font-size: 16px;
    line-height: 24px;
    color: #252525;
    margin: 0 0 12px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    &.note {
      font-style: italic;
      color: $color-text-light;
    }
  }
}

// Special Instructions Section
.special-instructions-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 10px;
  
  .section-label {
    font-family: $font-family-base;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: $color-black;
  }
  
  textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 12px;
    font-size: 16px;
    font-family: $font-family-base;
    border: 1px solid $color-border;
    border-radius: 5px;
    resize: vertical;
    
    &:focus {
      outline: none;
      border-color: $brand-primary;
    }
  }
}

// Navigation Buttons
.navigation-buttons {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #D9D9D9;
  
  @media (max-width: 768px) {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
}
</style>
